import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { seaOfKnowledgeService } from '../../services/seaOfKnowledgeService';
import { KnowledgeNode, NodeWithRelationships } from './types';
import PageHeader from '../common/PageHeader';
import KnowledgeGraph from './KnowledgeGraph';
import NodeDetail from './NodeDetail';
import './SeaOfKnowledge.css';

interface SeaOfKnowledgeProps {
  onToggleSidebar: () => void;
}

const SeaOfKnowledge: React.FC<SeaOfKnowledgeProps> = ({ onToggleSidebar }) => {
  const { user } = useAuth();
  const [nodes, setNodes] = useState<KnowledgeNode[]>([]);
  const [edges, setEdges] = useState<any[]>([]);
  const [selectedNode, setSelectedNode] = useState<NodeWithRelationships | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showMenu, setShowMenu] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [createPrompt, setCreatePrompt] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (user) {
      loadGraph();
      checkStarterNode();
    }
  }, [user]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  const loadGraph = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const result = await seaOfKnowledgeService.getGraph();
      
      if (result.success && result.data && result.data.nodes && result.data.edges) {
        setNodes(result.data.nodes);
        setEdges(result.data.edges);
      } else {
        setNodes([]);
        setEdges([]);
      }
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load graph');
      console.error('Error loading graph:', err);
    } finally {
      setLoading(false);
    }
  };

  const checkStarterNode = async () => {
    if (!user) return;
    
    try {
      const result = await seaOfKnowledgeService.getNodes();
      
      if (result.success && result.data && result.data.nodes && result.data.nodes.length === 0) {
        // Create starter node
        await seaOfKnowledgeService.createNode({
          question: 'What is the fundamental concept you want to learn?',
          description: 'This is your starting point. Answer this question to begin your knowledge journey.'
        });
        await loadGraph();
      }
    } catch (err) {
      console.error('Error checking starter node:', err);
    }
  };

  const handleNodeClick = async (nodeId: number) => {
    try {
      const result = await seaOfKnowledgeService.getNode(nodeId);
      if (result.success && result.data && result.data.node) {
        setSelectedNode(result.data.node);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load node details');
    }
  };

  const handleCreateNode = async () => {
    if (!createPrompt.trim()) {
      setError('Please enter a prompt');
      return;
    }

    try {
      const result = await seaOfKnowledgeService.generateNodeFromPrompt(createPrompt);
      if (result.success && result.data && result.data.node) {
        setCreatePrompt('');
        setShowCreateDialog(false);
        await loadGraph();
        // Select the new node
        if (result.data.node.id) {
          await handleNodeClick(result.data.node.id);
        }
      } else {
        setError(result.message || 'Failed to create node');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create node');
    }
  };

  const handleGenerateNode = async () => {
    if (!selectedNode) {
      setError('Please select a node first');
      return;
    }

    try {
      const result = await seaOfKnowledgeService.generateNodeFromContext(selectedNode.id);
      if (result.success && result.data && result.data.node) {
        setShowGenerateDialog(false);
        await loadGraph();
        // Select the new node
        if (result.data.node.id) {
          await handleNodeClick(result.data.node.id);
        }
      } else {
        setError(result.message || 'Failed to generate node');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to generate node');
    }
  };

  const handleNodeUpdated = async () => {
    await loadGraph();
    if (selectedNode) {
      await handleNodeClick(selectedNode.id);
    }
  };

  const handleCloseDetail = () => {
    setSelectedNode(null);
  };

  const renderRightActions = () => {
    return (
      <div className="sea-of-knowledge-header-actions" ref={menuRef}>
        <button
          className="sea-of-knowledge-menu-btn"
          onClick={() => setShowMenu(!showMenu)}
          title="Menu"
        >
          ⋯
        </button>
        {showMenu && (
          <div className="sea-of-knowledge-menu-dropdown">
            <button
              className="sea-of-knowledge-menu-item"
              onClick={() => {
                setShowCreateDialog(true);
                setShowMenu(false);
              }}
            >
              Create Node
            </button>
            <button
              className="sea-of-knowledge-menu-item"
              onClick={() => {
                if (selectedNode) {
                  setShowGenerateDialog(true);
                } else {
                  setError('Please select a node first');
                }
                setShowMenu(false);
              }}
              disabled={!selectedNode}
            >
              Generate Related Node
            </button>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="sea-of-knowledge-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<span>Sea of Knowledge</span>}
          rightActions={renderRightActions()}
        />
        <div className="sea-of-knowledge-loading">
          <p>Loading knowledge graph...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="sea-of-knowledge-container">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={<span>Sea of Knowledge</span>}
        rightActions={renderRightActions()}
      />
      
      {error && (
        <div className="sea-of-knowledge-error">
          <p>{error}</p>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className="sea-of-knowledge-content">
        <div className="sea-of-knowledge-graph-container">
          <KnowledgeGraph
            nodes={nodes}
            edges={edges}
            onNodeClick={handleNodeClick}
            selectedNodeId={selectedNode?.id}
          />
        </div>
      </div>

      {/* Node Detail Modal */}
      {selectedNode && (
        <div className="sea-of-knowledge-modal-overlay" onClick={handleCloseDetail}>
          <div className="sea-of-knowledge-modal-content" onClick={(e) => e.stopPropagation()}>
            <NodeDetail
              node={selectedNode}
              onClose={handleCloseDetail}
              onNodeUpdated={handleNodeUpdated}
              onGenerateNode={() => setShowGenerateDialog(true)}
            />
          </div>
        </div>
      )}

      {/* Create Node Dialog */}
      {showCreateDialog && (
        <div className="sea-of-knowledge-dialog-overlay" onClick={() => setShowCreateDialog(false)}>
          <div className="sea-of-knowledge-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Create New Node</h3>
            <p>Enter a prompt to generate a knowledge question:</p>
            <textarea
              className="sea-of-knowledge-dialog-input"
              value={createPrompt}
              onChange={(e) => setCreatePrompt(e.target.value)}
              placeholder="e.g., 'Explain quantum mechanics' or 'What is machine learning?'"
              rows={4}
            />
            <div className="sea-of-knowledge-dialog-actions">
              <button
                className="sea-of-knowledge-dialog-btn"
                onClick={() => setShowCreateDialog(false)}
              >
                Cancel
              </button>
              <button
                className="sea-of-knowledge-dialog-btn primary"
                onClick={handleCreateNode}
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Generate Node Dialog */}
      {showGenerateDialog && selectedNode && (
        <div className="sea-of-knowledge-dialog-overlay" onClick={() => setShowGenerateDialog(false)}>
          <div className="sea-of-knowledge-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Generate Related Node</h3>
            <p>Generate a new node related to:</p>
            <div className="sea-of-knowledge-dialog-node-preview">
              <strong>{selectedNode.question}</strong>
            </div>
            <div className="sea-of-knowledge-dialog-actions">
              <button
                className="sea-of-knowledge-dialog-btn"
                onClick={() => setShowGenerateDialog(false)}
              >
                Cancel
              </button>
              <button
                className="sea-of-knowledge-dialog-btn primary"
                onClick={handleGenerateNode}
              >
                Generate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SeaOfKnowledge;

